import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { useState } from "react"
import { operations, type DiscoverySchedule } from "./operationsApi"

export function CompanyAutomation({ companyId }: { companyId: string }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const cache = useQueryClient()
  const queryKey = [
    "company-index",
    user?.organization,
    "automation",
    companyId,
  ]
  const schedule = useQuery({
    queryKey,
    queryFn: () => operations.schedule(companyId),
  })
  const [draft, setDraft] = useState<Pick<
    DiscoverySchedule,
    "enabled" | "autoRepair" | "url" | "revision"
  > | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const values = draft ??
    schedule.data ?? { enabled: false, autoRepair: false, url: "", revision: 0 }
  const configure = useMutation({
    mutationFn: () =>
      operations.configure(companyId, {
        enabled: values.enabled,
        autoRepair: values.autoRepair,
        url: values.url,
        expectedRevision: values.revision,
      }),
    onSuccess: (data) => {
      cache.setQueryData(queryKey, data)
      setDraft(null)
      setConfirmed(false)
    },
  })
  const enqueue = useMutation({
    mutationFn: () => operations.enqueue(companyId, values.url),
    onSuccess: () => {
      void cache.invalidateQueries({
        queryKey: ["company-index", user?.organization, "operations"],
      })
    },
  })
  const canEdit = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  return (
    <section className="space-y-3 rounded-xl border p-5">
      <h2 className="font-semibold">{t("CI_AUTOMATION")}</h2>
      <p className="text-sm text-muted-foreground">{t("CI_AUTOMATION_HINT")}</p>
      {schedule.isPending && <p>{t("CI_LOADING")}</p>}
      {schedule.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void schedule.refetch()}>
            {t("CI_RETRY")}
          </Button>
        </p>
      )}
      {schedule.data && (
        <>
          <p>
            {t(
              schedule.data.enabled ? "CI_AUTOMATION_ON" : "CI_AUTOMATION_OFF",
            )}
            {schedule.data.enabled && schedule.data.nextDue
              ? ` · ${new Date(schedule.data.nextDue).toLocaleString()}`
              : ""}
          </p>
          {canEdit && (
            <>
              <label className="block">
                {t("CI_SCHEDULE_URL")}
                <Input
                  type="url"
                  aria-label={t("CI_SCHEDULE_URL")}
                  maxLength={4096}
                  value={values.url}
                  disabled={configure.isPending}
                  onChange={(e) => {
                    setDraft({ ...values, url: e.target.value })
                    setConfirmed(false)
                    configure.reset()
                  }}
                />
              </label>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={values.enabled}
                  disabled={configure.isPending}
                  onChange={(e) => {
                    setDraft({ ...values, enabled: e.target.checked })
                    setConfirmed(false)
                    configure.reset()
                  }}
                />
                {t("CI_WEEKLY_DISCOVERY")}
              </label>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={values.autoRepair}
                  disabled={configure.isPending}
                  onChange={(e) => {
                    setDraft({ ...values, autoRepair: e.target.checked })
                    setConfirmed(false)
                    configure.reset()
                  }}
                />
                {t("CI_AUTO_REPAIR")}
              </label>
              <p className="text-sm text-muted-foreground">
                {t("CI_REPAIR_HINT")}
              </p>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t("CI_AUTOMATION_CONFIRM")}
              </label>
              <Button
                disabled={
                  configure.isPending ||
                  !draft ||
                  !/^https?:\/\//.test(values.url) ||
                  ((values.enabled || values.autoRepair) && !confirmed)
                }
                onClick={() => configure.mutate()}
              >
                {t("CI_SAVE_AUTOMATION")}
              </Button>
            </>
          )}
        </>
      )}
      {canEdit && (
        <Button
          variant="outline"
          disabled={
            enqueue.isPending ||
            !schedule.isSuccess ||
            !/^https?:\/\//.test(values.url)
          }
          onClick={() => enqueue.mutate()}
        >
          {t("CI_QUEUE_DISCOVERY")}
        </Button>
      )}
      {(configure.isError || enqueue.isError) && (
        <p role="alert">
          {t(
            [configure.error, enqueue.error].some(
              (error) =>
                axios.isAxiosError(error) && error.response?.status === 422,
            )
              ? "CI_AUTOMATION_URL_ERROR"
              : "CI_OPERATIONS_ERROR",
          )}
        </p>
      )}
      {configure.isError && (
        <Button
          variant="outline"
          onClick={() => {
            setDraft(null)
            setConfirmed(false)
            configure.reset()
            void schedule.refetch()
          }}
        >
          {t("CI_REFRESH")}
        </Button>
      )}
      {configure.isSuccess && <p role="status">{t("CI_AUTOMATION_SAVED")}</p>}
      {enqueue.isSuccess && <p role="status">{t("CI_JOB_QUEUED")}</p>}
    </section>
  )
}
